import {
  Application,
  Container,
  Graphics,
  GraphicsContext,
  Rectangle,
  RenderTexture,
  Sprite,
  Texture,
} from 'pixi.js';
// Registers the WebGL "prepare" system on the renderer (side-effect only; must
// run before Application.init() below). Types for renderer.prepare come from
// pixi.js's own root d.ts (which always references PrepareMixins), so this
// import is runtime-only — see warmupTextures().
import 'pixi.js/prepare';
import { loadLayerStack, setLayerStackAtlasMode } from '../layerstack/LayerStackLoader';
import { LayerStackSprite, PULSE_FPS } from '../layerstack/LayerStackSprite';
import { isMobilePerformanceDevice } from '../../platform/mobilePerformance';
import { ImpactPostFilter } from './postfx/ImpactPostFilter';

/**
 * The R2 layer-stack render path: a 960x540 logical canvas at an integer
 * display scale, three parallax background bands, and actors drawn as
 * LayerStackSprite. It lives beside PixiRenderer rather than inside it — the
 * old 16x16 atlas path is untouched and stays the default until the R2 flag
 * flips (see r2Flag.ts).
 */
export const R2_WIDTH = 960;
export const R2_HEIGHT = 540;
/** Far to near; the near band moves the most. Three bands, matching the stage set. */
export const R2_BAND_SPEEDS = Object.freeze([0.08, 0.31, 0.76]);
const BACKGROUND_COLOR = 0x02030a;

/**
 * Camera shake, trauma model. Amplitude is trauma squared so small hits barely
 * register and a kill snaps; the offset comes from two decoupled sine
 * oscillators rather than an RNG, so a run repeats. Vertical is biased
 * non-negative: bands are bottom-aligned, and a downward shift hides the frame
 * edge under the near band instead of opening a background gap along it.
 */
const TRAUMA_MAX = 1;
const SHAKE_MAX_PX = 6;
const TRAUMA_DECAY_PER_SECOND = 1.8;
const SHAKE_HZ_X = 23;
const SHAKE_HZ_Y = 17;
const MS_PER_SECOND = 1000;
const TAU = Math.PI * 2;

/**
 * Full-frame post pass. The chromatic split has a rest level rather than
 * sitting at zero: a lens that only misbehaves on impact reads as a glitch,
 * while a quarter-pixel fringe that widens to a pixel and a half reads as the
 * same lens being hit. The vignette is static and deliberately below the
 * threshold where anyone would name it — it exists to stop the frame edge from
 * competing with the playfield.
 *
 * flashReduction zeroes both the rest fringe and the pulse; the vignette stays,
 * because a fixed corner darkening never flashes.
 */
const POST_CA_REST_PX = 0.25;
const POST_CA_PEAK_PX = 1.5;
const POST_VIGNETTE = 0.18;

/**
 * バンド上端のアルファフェード（水平継ぎ目の解消）。
 *
 * バンドは不透明な矩形なので、下端揃えのバンドは上端がそのまま画面中ほどの
 * 水平線として出る（旧「三段重ね」）。上端から一定距離をかけて透過させ、
 * 後ろのバンドへ溶かすことで線を消す。
 *
 * オーバースキャンは trauma 振動（最大 SHAKE_MAX_PX）でマスクの左右・下端が
 * 画面内へ入り込まないための余白。マスクは row コンテナの子に置くので、
 * 振動・ズームは root ごと乗り、バンドとマスクの相対位置は動かない。
 */
const FADE_MASK_OVERSCAN_PX = 16;
/** 横は引き伸ばして使う（行方向は一定値）ので幅は表現に関与しない。 */
const FADE_MASK_TEXTURE_WIDTH = 8;
const FADE_MASK_SOLID_FILL = '#ffffff';
const RGB_MAX = 255;
const SMOOTHSTEP_A = 3;
const SMOOTHSTEP_B = 2;

/** 0..1 の滑らかな立ち上がり。線形だとフェードの開始/終了が両端で折れて見える。 */
function smoothstep01(t: number): number {
  return t * t * (SMOOTHSTEP_A - SMOOTHSTEP_B * t);
}

/**
 * 上端フェード用のマスク素材を 1 枚描く。**不透明のグレースケール**で作るのが
 * 要点で、階調をアルファ側に持たせてはならない: Pixi 8 の mask.frag は既定の
 * red チャンネルで `a = (1 - uAlpha * (1 - masky.a)) * masky.r` を掛けるため、
 * アルファ階調は乗算済みアルファ（rgb = a）と二重に効いて勾配が非線形に潰れる。
 * α=1 固定で輝度だけを動かせば alphaMul は恒等 1 になり、係数はちょうど
 * masky.r ＝ 描いた勾配そのものになる。
 */
function createTopFadeCanvas(fadePx: number, height: number): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  canvas.width = FADE_MASK_TEXTURE_WIDTH;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const fadeRows = Math.min(fadePx, height);
  for (let row = 0; row < fadeRows; row += 1) {
    const level = Math.round(RGB_MAX * smoothstep01((row + 0.5) / fadeRows));
    ctx.fillStyle = `rgb(${level}, ${level}, ${level})`;
    ctx.fillRect(0, row, FADE_MASK_TEXTURE_WIDTH, 1);
  }
  ctx.fillStyle = FADE_MASK_SOLID_FILL;
  ctx.fillRect(0, fadeRows, FADE_MASK_TEXTURE_WIDTH, height - fadeRows);
  return canvas;
}

/** How a band's row continues past one master width. */
export type BandWrapMode = 'repeat' | 'mirror';

export interface BandSpec {
  readonly rungPath: string;
  /** Scroll factor against R2Renderer.scrollX. */
  readonly speed: number;
  /**
   * Top edge in logical pixels; bottom-aligned when omitted. Negative is legal
   * and crops that much of the master off the top of the canvas.
   */
  readonly y?: number;
  readonly alpha?: number;
  /**
   * 'repeat' (the default) butts whole copies side by side. 'mirror' ping-pongs
   * them instead — every odd global tile is flipped horizontally, so each wrap
   * seam meets its own reflection and reads continuous.
   */
  readonly wrapMode?: BandWrapMode;
  /**
   * 描かないレイヤー名。中景/近景の不透明な空フィル層を落とし、空の描画を
   * far バンド一枚に統一する（バンド積層の水平段差の根治）。
   */
  readonly hiddenLayerNames?: readonly string[];
  /**
   * Distance between two copies, defaulting to the master width (copies butt
   * together and the row is continuous). A span wider than the master leaves a
   * gap: that is how a sparse feature layer — one landmark every N pixels — is
   * expressed without a second band class.
   */
  readonly spanWidth?: number;
  /**
   * 上端から何 px かけて透過させるか（省略 = フェードなし）。不透明なバンドの
   * 上端が水平の継ぎ目として見えるのを、後ろのバンドへ溶かして消すための量。
   * 上端が画面外にあるバンド（far は y=0 固定）と、もともと透過を持つ素材
   * （ランドマーク）には不要。
   */
  readonly topFadePx?: number;
}

export interface ActorSpec {
  readonly rungPath: string;
  readonly x: number;
  readonly y: number;
  readonly state?: string;
  readonly mirror?: boolean;
}

/**
 * One background band. The R2 masters are not tileable, so the band repeats a
 * whole copy of the stack side by side and wraps the row on the master width:
 * the seam is a property of the art, not of the scroll. 'mirror' wrapping hides
 * that seam without touching the art — a flipped copy presents its own right
 * edge to the previous copy's right edge, which matches by construction.
 */
class ParallaxBand {
  readonly container = new Container();
  readonly copies: LayerStackSprite[] = [];
  readonly speed: number;
  private readonly spanWidth: number;
  private readonly topY: number;
  private readonly mirrorWrap: boolean;

  constructor(sprites: LayerStackSprite[], speed: number, spanWidth: number, topY: number, alpha: number, wrapMode: BandWrapMode) {
    this.speed = speed;
    this.spanWidth = spanWidth;
    this.topY = topY;
    this.mirrorWrap = wrapMode === 'mirror';
    this.container.alpha = alpha;
    for (const sprite of sprites) {
      this.copies.push(sprite);
      this.container.addChild(sprite);
    }
    this.setScroll(0);
  }

  static copyCount(spanWidth: number): number {
    return Math.ceil(R2_WIDTH / spanWidth) + 1;
  }

  /**
   * Scrolls left with the camera; the row wraps on the master width, in whole
   * pixels. A copy slot is only a recycled slot — the tile it shows is the
   * global index `wraps + i`, which advances every time the row wraps past a
   * master width. Mirror parity has to follow that global index, never the
   * array slot, or the ping-pong would invert itself on every wrap.
   */
  setScroll(scrollX: number): void {
    const shift = Math.floor(scrollX * this.speed);
    const wraps = Math.floor(shift / this.spanWidth);
    const origin = wraps * this.spanWidth - shift;
    for (let i = 0; i < this.copies.length; i += 1) {
      const tile = wraps + i;
      const sprite = this.copies[i]!;
      sprite.setPixelPosition(origin + i * this.spanWidth, this.topY);
      sprite.setMirror(this.mirrorWrap && (((tile % 2) + 2) % 2) === 1);
    }
  }

  advance(deltaMs: number): void {
    for (const sprite of this.copies) sprite.advance(deltaMs);
  }

  /**
   * 上端フェードのマスクを張る。row コンテナ自身は恒久的に原点で、動くのは
   * その子のコピー側（setScroll）なので、マスクを子に置けば横スクロールから
   * 独立したスクリーン空間の固定物になる。
   *
   * `boundsArea` も同時に固定する。マスク（＝フィルタ）の測定領域は既定では
   * コピー列全体（マスター幅の 2〜3 倍）になり、その大きさの RT が確保される
   * ため。矩形はマスク自身の寸法から作る — 2 箇所に別々の式で書くと、ずれた
   * ときに「振動中だけ画面端が切れる」という再現しにくい不一致になる。
   */
  attachTopFade(mask: Sprite): void {
    this.container.addChild(mask);
    this.container.mask = mask;
    this.container.boundsArea = new Rectangle(mask.x, mask.y, mask.width, mask.height);
  }

  /** マスクは子なので children ごと落ちるが、参照を外してから壊す。 */
  destroyRow(): void {
    this.container.mask = null;
    this.container.destroy({ children: true });
  }
}

export class R2Renderer {
  rendererName = 'uninitialized';
  scrollX = 0;
  private app: Application | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private disposed = false;
  private root: Container | null = null;
  private postRoot: Container | null = null;
  private postFilter: ImpactPostFilter | null = null;
  private postReduced = false;
  private impactPulse = 0;
  private musicPulse = 0;
  private bandLayer: Container | null = null;
  private actorLayer: Container | null = null;
  private fxLayer: Container | null = null;
  private readonly bands: ParallaxBand[] = [];
  private readonly actors: LayerStackSprite[] = [];
  private readonly topFadeTextures = new Map<string, Texture>();
  private displayScale = 1;
  private traumaValue = 0;
  private shakeClockMs = 0;
  private rootOffsetX = 0;
  private rootOffsetY = 0;
  private impactScale = 1;
  private musicScale = 1;
  private resolveDisposedSignal: (() => void) | null = null;
  private readonly disposedSignal = new Promise<void>((resolve) => {
    this.resolveDisposedSignal = resolve;
  });
  readonly mobilePerformanceMode = isMobilePerformanceDevice();

  async init(canvas: HTMLCanvasElement): Promise<void> {
    if (this.disposed) return;
    setLayerStackAtlasMode(this.mobilePerformanceMode);
    const app = new Application();
    await app.init({
      canvas,
      width: R2_WIDTH,
      height: R2_HEIGHT,
      resolution: 1,
      autoDensity: false,
      autoStart: false,
      sharedTicker: false,
      preference: ['webgl'],
      webgl: { preferWebGLVersion: 2, powerPreference: 'high-performance', preserveDrawingBuffer: false },
      antialias: false,
      roundPixels: true,
      backgroundColor: BACKGROUND_COLOR,
      clearBeforeRender: true,
      hello: false,
      ...(this.mobilePerformanceMode
        ? {
            // Stage bundles release known-dead atlases explicitly. Keep Pixi's
            // global sweep away from combat cadence instead of rescanning the
            // texture graph every default 30 seconds.
            gcActive: true,
            gcFrequency: 120_000,
            gcMaxUnusedTime: 180_000,
          }
        : {}),
    });
    if (this.disposed) {
      app.destroy(true);
      return;
    }
    app.ticker.stop();
    canvas.width = R2_WIDTH;
    canvas.height = R2_HEIGHT;
    canvas.style.imageRendering = 'pixelated';
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', 'LUMINA ARCHIVE R2 layer-stack display');
    const gl2 = canvas.getContext('webgl2');
    if (!gl2) {
      app.destroy(true);
      throw new Error('WebGL2 is required but unavailable.');
    }
    this.app = app;
    this.canvas = canvas;
    this.rendererName = `WebGL2 / PixiJS ${R2_WIDTH}x${R2_HEIGHT} (R2 layer stacks)`;
    const root = new Container();
    const bandLayer = new Container();
    const actorLayer = new Container();
    // FX above characters, below UI — the ref sheet's layering. Note that
    // addOverlay() appends after this, so a host that draws its entities into
    // an overlay (R2GameRenderer) currently lands above fxLayer; reconciling
    // that belongs to the wave that wires FxDirector into the game loop.
    const fxLayer = new Container();
    root.addChild(bandLayer, actorLayer, fxLayer);
    // A neutral container between the stage and the shake root. The post pass
    // has to sit on something that never moves: on `root` the vignette corners
    // would drift with every impact zoom, and on the stage it would be a filter
    // on the render-group root. `postRoot` keeps an identity transform forever,
    // and its filterArea is pinned so the pass never re-measures scene bounds.
    const postRoot = new Container();
    postRoot.addChild(root);
    app.stage.addChild(postRoot);
    this.root = root;
    this.postRoot = postRoot;
    this.bandLayer = bandLayer;
    this.actorLayer = actorLayer;
    this.fxLayer = fxLayer;
    if (!this.mobilePerformanceMode) this.attachPostFilter(postRoot);
    this.setDisplayScale(this.displayScale);
  }

  /** A cosmetic pass must never take the renderer down: headless and CI WebGL vary. */
  private attachPostFilter(postRoot: Container): void {
    try {
      const filter = new ImpactPostFilter(R2_WIDTH, R2_HEIGHT, POST_VIGNETTE);
      postRoot.filterArea = new Rectangle(0, 0, R2_WIDTH, R2_HEIGHT);
      postRoot.filters = [filter];
      this.postFilter = filter;
      this.applyPostStrength();
    } catch {
      this.postFilter = null;
    }
  }

  /** The backing store stays 960x540; only the CSS box grows, by whole pixels. */
  setDisplayScale(scale: number): void {
    const integerScale = Math.max(1, Math.floor(scale));
    this.displayScale = integerScale;
    const canvas = this.canvas;
    if (!canvas) return;
    canvas.style.width = `${R2_WIDTH * integerScale}px`;
    canvas.style.height = `${R2_HEIGHT * integerScale}px`;
  }

  get scale(): number {
    return this.displayScale;
  }

  async addBand(spec: BandSpec): Promise<void> {
    const layer = this.bandLayer;
    if (!layer) throw new Error('R2Renderer.addBand before init');
    const asset = await loadLayerStack(spec.rungPath);
    if (this.disposed || this.bandLayer !== layer) return;
    const probe = new LayerStackSprite(asset, this.mobilePulsePhase(0));
    const sprites = [probe];
    const spanWidth = spec.spanWidth ?? probe.targetWidth;
    for (let i = 1; i < ParallaxBand.copyCount(spanWidth); i += 1) {
      sprites.push(new LayerStackSprite(asset, this.mobilePulsePhase(i)));
    }
    const hidden = spec.hiddenLayerNames;
    if (hidden && hidden.length > 0) for (const sprite of sprites) sprite.setHiddenLayerNames(hidden);
    const topY = spec.y ?? R2_HEIGHT - probe.targetHeight;
    const band = new ParallaxBand(sprites, spec.speed, spanWidth, topY, spec.alpha ?? 1, spec.wrapMode ?? 'repeat');
    this.applyTopFade(band, spec.topFadePx ?? 0, topY);
    this.bands.push(band);
    layer.addChild(band.container);
    band.setScroll(this.scrollX);
  }

  /**
   * 上端フェードのマスクを 1 枚だけ用意して張る。マスクはバンドの可視範囲を
   * 完全に覆う必要がある — Pixi のスプライトマスクは素材の枠外を切り落とすため、
   * 覆い損ねた領域はフェードではなく消失になる。
   */
  private applyTopFade(band: ParallaxBand, fadePx: number, topY: number): void {
    // Sprite masks allocate additional render work. Desktop keeps the authored
    // band blend; mobile accepts the already-hidden sky-fill layers as-is.
    if (fadePx <= 0 || this.mobilePerformanceMode) return;
    const width = R2_WIDTH + FADE_MASK_OVERSCAN_PX * 2;
    const height = R2_HEIGHT + FADE_MASK_OVERSCAN_PX - topY;
    const texture = this.topFadeTexture(fadePx, height);
    if (!texture) return;
    const mask = new Sprite(texture);
    mask.position.set(-FADE_MASK_OVERSCAN_PX, topY);
    mask.width = width;
    mask.height = height;
    band.attachTopFade(mask);
  }

  /**
   * (フェード量, 縦の実寸) ごとに 1 枚だけ生成して使い回す。単一の共有ランプを
   * 引き伸ばす方式にしないのは、その場合フェード幅と全体高さの比がテクスチャ側で
   * 固定され、`topFadePx` をピクセル単位で正確に出せないため。実寸ちょうどで作れば
   * 縦倍率は 1 のままで、テクセルと論理ピクセルが 1:1 に乗る。実際に生成されるのは
   * 中景・近景の 2 枚だけ（いずれも幅 8px）。
   */
  private topFadeTexture(fadePx: number, height: number): Texture | null {
    const key = `${fadePx}:${height}`;
    const cached = this.topFadeTextures.get(key);
    if (cached) return cached;
    const canvas = createTopFadeCanvas(fadePx, height);
    if (!canvas) return null;
    const texture = Texture.from(canvas);
    this.topFadeTextures.set(key, texture);
    return texture;
  }

  async addActor(spec: ActorSpec): Promise<LayerStackSprite> {
    const layer = this.actorLayer;
    if (!layer) throw new Error('R2Renderer.addActor before init');
    const asset = await loadLayerStack(spec.rungPath);
    const sprite = new LayerStackSprite(asset);
    sprite.setPixelPosition(spec.x, spec.y);
    if (spec.state !== undefined) sprite.setState(spec.state);
    if (spec.mirror !== undefined) sprite.setMirror(spec.mirror);
    if (this.disposed || this.actorLayer !== layer) return sprite;
    this.actors.push(sprite);
    layer.addChild(sprite);
    return sprite;
  }

  /**
   * Hangs a caller-owned display object above the bands, inside the shake root.
   * The host keeps ownership: clearScene() and dispose() do not touch it.
   */
  addOverlay(child: Container): void {
    const root = this.root;
    if (!root) throw new Error('R2Renderer.addOverlay before init');
    root.addChild(child);
  }

  /**
   * Whole-scene offset in logical pixels — the host's own screen shake. It is
   * independent of the trauma shake and adds to it: a host may drive this every
   * frame while an FX hit is still ringing, and neither may cancel the other.
   * Rounding happens once, on the sum, in applyRootPosition().
   */
  setRootOffset(x: number, y: number): void {
    this.rootOffsetX = x;
    this.rootOffsetY = y;
    this.applyRootPosition();
  }

  /**
   * Whole-scene zoom about the canvas centre — the impact punch. It is a third
   * independent term next to the host offset and the trauma shake: this only
   * stores the scale, and applyRootPosition() composes all three in one place.
   * The centre is held fixed by compensating the position rather than by moving
   * the pivot, because the pivot is in the root's own (scaled) space while the
   * two offsets are in parent space, and mixing the two would make the shake
   * amplitude depend on the zoom.
   */
  setImpactZoom(scale: number): void {
    this.impactScale = scale;
    this.applyRootPosition();
  }

  /** 134 BPM演出の全景パンチ。衝撃ズームとは別項で保持し、最終段だけ安全上限へ束ねる。 */
  setMusicPulseZoom(scale: number): void {
    this.musicScale = scale;
    this.applyRootPosition();
  }

  /**
   * The chromatic-aberration envelope, 0 at rest and 1 at a full punch. It is a
   * normalised level rather than a pixel width so the caller drives it off the
   * same impact envelope that feeds setImpactZoom without knowing what a
   * sensible fringe is in pixels — that number lives here, with the other
   * presentation constants.
   */
  setImpactPulse(level: number): void {
    this.impactPulse = level < 0 ? 0 : level > 1 ? 1 : level;
    this.applyPostStrength();
  }

  setMusicPulse(level: number): void {
    this.musicPulse = level < 0 ? 0 : level > 1 ? 1 : level;
    this.applyPostStrength();
  }

  /** Mirrors settings.flashReduction into the post pass: no rest fringe, no pulse, vignette untouched. */
  setPostReduction(reduced: boolean): void {
    this.postReduced = reduced;
    this.applyPostStrength();
  }

  private applyPostStrength(): void {
    const filter = this.postFilter;
    if (!filter) return;
    if (this.postReduced) {
      filter.setStrength(0);
      return;
    }
    filter.setStrength(
      POST_CA_REST_PX
      + (POST_CA_PEAK_PX - POST_CA_REST_PX) * Math.max(this.impactPulse, this.musicPulse),
    );
  }

  get bandSprites(): readonly LayerStackSprite[] {
    const all: LayerStackSprite[] = [];
    for (const band of this.bands) for (const sprite of band.copies) all.push(sprite);
    return all;
  }

  get actorSprites(): readonly LayerStackSprite[] {
    return this.actors;
  }

  /** FX containers own their own sprites; the renderer only lends them the layer. */
  addFx(child: Container): void {
    const layer = this.fxLayer;
    if (!layer) throw new Error('R2Renderer.addFx before init');
    layer.addChild(child);
  }

  get trauma(): number {
    return this.traumaValue;
  }

  addTrauma(amount: number): void {
    if (amount <= 0) return;
    this.traumaValue = Math.min(TRAUMA_MAX, this.traumaValue + amount);
  }

  /** Advances every pulse cycle, re-lays the parallax rows and shakes the camera. */
  update(deltaMs: number): void {
    for (const band of this.bands) {
      band.advance(deltaMs);
      band.setScroll(this.scrollX);
    }
    for (const actor of this.actors) actor.advance(deltaMs);
    this.applyShake(deltaMs);
  }

  /** The delta is already timebase-scaled, so a hit stop freezes the decay too. */
  private applyShake(deltaMs: number): void {
    this.shakeClockMs += deltaMs;
    this.traumaValue = Math.max(0, this.traumaValue - (deltaMs / MS_PER_SECOND) * TRAUMA_DECAY_PER_SECOND);
    this.applyRootPosition();
  }

  /**
   * The one place the three independent terms meet, and the one rounding. A
   * point p in root space lands at p * scale + position, so holding the canvas
   * centre c fixed means adding c * (1 - scale) to the position — at scale 1
   * that term is exactly 0 and this reduces to the offsets alone.
   *
   * Whole-pixel snapping is relaxed while a zoom is ringing: the compensation
   * is fractional by construction and the scaled content no longer lands on
   * whole pixels anyway, so rounding there would only add jitter to the frame
   * edge. The moment the punch decays back to exactly 1, snapping resumes.
   */
  private applyRootPosition(): void {
    const root = this.root;
    if (!root) return;
    const amplitude = this.traumaValue * this.traumaValue * SHAKE_MAX_PX;
    const seconds = this.shakeClockMs / MS_PER_SECOND;
    const swayX = Math.sin(TAU * SHAKE_HZ_X * seconds);
    const swayY = (Math.sin(TAU * SHAKE_HZ_Y * seconds) + 1) / 2;
    const scale = Math.min(1.09, this.impactScale * this.musicScale);
    const x = this.rootOffsetX + amplitude * swayX + (R2_WIDTH / 2) * (1 - scale);
    const y = this.rootOffsetY + amplitude * swayY + (R2_HEIGHT / 2) * (1 - scale);
    root.scale.set(scale);
    if (scale === 1) root.position.set(Math.round(x), Math.round(y));
    else root.position.set(x, y);
  }

  render(): void {
    const app = this.app;
    if (!app) return;
    app.renderer.render(app.stage);
  }

  /**
   * Uploads every given texture to the GPU ahead of its first appearance on
   * screen. Without this, LayerStackSprite's lazy texture assignment means the
   * first time any given enemy state / boss / laser raster is actually drawn,
   * Pixi does the glTexImage2D upload synchronously inline with that frame —
   * a hitch exactly when something new shows up. `renderer.prepare` (enabled
   * by the `pixi.js/prepare` import above) walks a plain array of Texture and
   * uploads each one without needing a Sprite on screen referencing it, so
   * this runs once at init and costs nothing per frame after.
   */
  async warmupTextures(textures: readonly Texture[]): Promise<void> {
    const app = this.app;
    if (!app || textures.length === 0) return;
    await Promise.race([
      app.renderer.prepare.upload([...textures]),
      this.disposedSignal,
    ]);
  }

  /**
   * Forces Pixi to tessellate every immutable mobile GraphicsContext before
   * combat. PrepareSystem only discovers GraphicsContext resources; the public
   * render path is what actually builds their geometry batches.
   *
   * Temporary Graphics must detach from the externally owned contexts before
   * teardown because GraphicsContext retains update/unload listeners. A single
   * sacrificial context receives those listeners and is destroyed immediately.
   */
  warmupGraphicsContexts(contexts: readonly GraphicsContext[]): void {
    const app = this.app;
    if (!app || contexts.length === 0 || this.disposed) return;
    const target = new Container();
    const leaves: Graphics[] = [];
    const sacrificial = new GraphicsContext();
    const renderTexture = RenderTexture.create({
      width: 1,
      height: 1,
      resolution: 1,
    });
    try {
      for (let i = 0; i < contexts.length; i += 1) {
        const leaf = new Graphics({
          context: contexts[i]!,
          roundPixels: true,
        });
        leaves.push(leaf);
        target.addChild(leaf);
      }
      app.renderer.render({
        container: target,
        target: renderTexture,
        clear: true,
      });
    } finally {
      for (let i = 0; i < leaves.length; i += 1) {
        leaves[i]!.context = sacrificial;
      }
      target.destroy({ children: true });
      sacrificial.destroy();
      renderTexture.destroy(true);
    }
  }

  private mobilePulsePhase(index: number): number {
    return this.mobilePerformanceMode ? (index % 8) * ((1000 / PULSE_FPS) / 8) : 0;
  }

  /**
   * Destroys the FX containers with the rest of the scene: whoever handed them
   * over must drop its reference first, or it will advance destroyed sprites.
   * Overlays added through addOverlay() stay — the host owns those.
   */
  clearScene(): void {
    for (const band of this.bands) band.destroyRow();
    // バンドを壊した後に落とす（逆順だと破棄済みの素材を参照したまま描画に入る）。
    for (const texture of this.topFadeTextures.values()) texture.destroy(true);
    this.topFadeTextures.clear();
    for (const actor of this.actors) actor.destroy({ children: true });
    const fxLayer = this.fxLayer;
    if (fxLayer) for (const child of [...fxLayer.children]) child.destroy({ children: true });
    this.bands.length = 0;
    this.actors.length = 0;
    this.scrollX = 0;
    this.traumaValue = 0;
    this.shakeClockMs = 0;
    this.rootOffsetX = 0;
    this.rootOffsetY = 0;
    this.impactScale = 1;
    this.impactPulse = 0;
    this.musicScale = 1;
    this.musicPulse = 0;
    this.applyPostStrength();
    if (this.root) {
      this.root.scale.set(1);
      this.root.position.set(0, 0);
    }
  }

  dispose(): void {
    this.disposed = true;
    this.resolveDisposedSignal?.();
    this.resolveDisposedSignal = null;
    const app = this.app;
    this.clearScene();
    // The filter is not a child of postRoot, so app.destroy() will not reach it.
    const postFilter = this.postFilter;
    this.postFilter = null;
    if (this.postRoot) this.postRoot.filters = [];
    if (postFilter) postFilter.destroy();
    this.app = null;
    this.canvas = null;
    this.root = null;
    this.postRoot = null;
    this.bandLayer = null;
    this.actorLayer = null;
    this.fxLayer = null;
    if (app) app.destroy(true);
  }
}
